/**
 * Playback failures split into two populations, and only one of them is a
 * defect anybody can act on.
 *
 * A slow connection, an aborted load, or a blocked autoplay is a property of
 * the *visitor's session*. It recurs on every affected play, so wiring it to a
 * Sentry event bills telemetry in proportion to traffic instead of in
 * proportion to the number of distinct problems — the same shape that made
 * `getAudioUrl` 70% of this project's error volume (TES-1977).
 *
 * A decode failure or an unsupported/missing source is a property of the
 * *asset*: that file is broken in the catalog. That is worth an event, but its
 * information content is one bit per file, not one per play. Reporting it once
 * per asset per page session carries the whole signal, and the
 * `check-audio-links` cron is the table-wide sweep for the same class of
 * defect.
 */

export type AudioFailureKind = 'session-condition' | 'asset-defect' | 'unknown'

/** `HTMLMediaError.code` values, which are not exposed as constants off the class. */
const MEDIA_ERR_ABORTED = 1
const MEDIA_ERR_NETWORK = 2
const MEDIA_ERR_DECODE = 3
const MEDIA_ERR_SRC_NOT_SUPPORTED = 4

/**
 * `play()` rejections that describe the visitor's environment. `AbortError`
 * never reaches here (the hook drops it as a non-fatal source switch), but it
 * is listed so a future caller that does pass it classifies the same way.
 */
const SESSION_CONDITION_ERROR_NAMES = new Set(['AbortError', 'NotAllowedError'])

export function classifyMediaErrorCode(code: number): AudioFailureKind {
  switch (code) {
    case MEDIA_ERR_ABORTED:
    case MEDIA_ERR_NETWORK:
      return 'session-condition'
    case MEDIA_ERR_DECODE:
    case MEDIA_ERR_SRC_NOT_SUPPORTED:
      return 'asset-defect'
    default:
      return 'unknown'
  }
}

export function classifyPlayRejection(error: { name?: string; message?: string }): AudioFailureKind {
  if (error.name && SESSION_CONDITION_ERROR_NAMES.has(error.name)) return 'session-condition'
  // The hook's own 10s load timeout. A visitor on a slow link hits it on every
  // play, which is exactly the traffic-proportional shape to keep out of Sentry.
  if (error.message?.includes('timeout')) return 'session-condition'
  if (error.name === 'NotSupportedError') return 'asset-defect'
  return 'unknown'
}

/**
 * Distinct (kind, src) pairs already reported by this page session. Module
 * scope rather than a ref because the dedupe has to hold across every player
 * instance on the page — the /sounds grid mounts one per card, and a broken
 * asset in a shared queue would otherwise report once per card that touched it.
 */
const reported = new Set<string>()

/**
 * True the first time this (kind, src) pair fails in this page session.
 * `session-condition` never reports.
 */
export function shouldReportAudioFailure(kind: AudioFailureKind, src: string | undefined): boolean {
  if (kind === 'session-condition') return false

  const key = `${kind}:${src ?? '<no-src>'}`
  if (reported.has(key)) return false
  reported.add(key)
  return true
}

/** Test seam — the dedupe set is module state and would leak between cases. */
export function resetAudioFailureReports(): void {
  reported.clear()
}

/** Sentry caps a tag value at 200 characters and truncates silently past it. */
const MAX_TAG_LENGTH = 200

/**
 * The storage path of a failing asset, as a Sentry *tag* so the cluster can be
 * grouped and searched by file rather than read one event at a time (TES-1874).
 *
 * The pathname only: a resolved audio URL can carry a signed token or a cache
 * buster, and either would give every play its own tag value and destroy the
 * grouping this exists for. The full URL still travels in `extra`.
 */
export function audioSrcTag(src: string | undefined): string {
  if (!src) return '<no-src>'
  let path = src
  try {
    path = new URL(src).pathname
  } catch {
    // Deliberately no base URL: a relative src (the bundled demo audio, the
    // proxy route) is already the path this wants, and anything else unparseable
    // is itself diagnostic. Either way, tag it verbatim rather than dropping the
    // only identifier we have.
  }
  return path.slice(0, MAX_TAG_LENGTH)
}
